import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { MapPin, Phone, Mail, Clock } from "lucide-react";
import { api, errorMessage, mediaUrl } from "@/lib/api";
import { treatmentPath } from "@/lib/format";
import { useSite } from "@/context/AppContext";
import { Container, PageHero, Prose, Seo, SectionTitle } from "@/components/site/Ui";
import BeforeAfter from "@/components/site/BeforeAfter";
import { useFetch, Loading, NotFound } from "./Catalog";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

export function About() {
  const page = useFetch("/public/pages/chi-siamo");
  const team = useFetch("/public/team");
  if (page.loading) return <Loading />;
  const p = page.data || { title: "Chi siamo", intro: "", body_html: "" };
  return (
    <>
      <Seo title={p.seo_title || "Chi siamo"} description={p.seo_description || p.intro} path="/chi-siamo" />
      <PageHero eyebrow="Chi siamo" title={p.title} intro={p.intro} crumbs={[{ label: "Chi siamo" }]} />
      <Container className="grid gap-14 py-20 lg:grid-cols-2">
        <Prose html={p.body_html} />
        <img src="/img/centro-7289.webp" alt="Ingresso e sala d'attesa luminosa del centro" width={1800} height={1200} loading="lazy" className="aspect-[4/3] w-full rounded-[2rem] object-cover" />
      </Container>
      <section className="bg-[var(--ivory-soft)] py-20" aria-labelledby="equipe">
        <Container>
          <SectionTitle eyebrow="Équipe" title="Le persone del centro" />
          <div className="mt-12 grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
            {(team.data || []).map((m) => (
              <article key={m.id} className="card-soft overflow-hidden" data-testid={`team-member-${m.id}`}>
                {m.photo_media_id ? <img src={mediaUrl(m.photo_media_id, "md")} alt={m.name} width={960} height={1200} loading="lazy" className="aspect-[4/5] w-full object-cover" /> : <div className="flex aspect-[4/5] items-center justify-center bg-[var(--powder-light)] text-sm text-[var(--muted-ink)]">[Foto da inserire]</div>}
                <div className="p-6"><h3 className="text-2xl">{m.name}</h3><p className="eyebrow mt-2">{m.title}</p><p className="mt-3 text-sm text-[var(--muted-ink)]">{m.bio}</p></div>
              </article>
            ))}
          </div>
        </Container>
      </section>
    </>
  );
}

function MapEmbed({ url, link }) {
  const [load, setLoad] = useState(false);
  if (!url) return <div className="flex aspect-[4/3] items-center justify-center rounded-2xl border border-dashed border-[var(--brass)] text-sm text-[var(--muted-ink)]">[Mappa da configurare nel pannello]</div>;
  if (!load) {
    return (
      <div className="flex aspect-[4/3] flex-col items-center justify-center gap-4 rounded-2xl bg-[var(--powder-light)] p-6 text-center text-sm">
        <p>La mappa è fornita da Google e può impostare cookie di terze parti.</p>
        <button className="btn-primary" onClick={() => setLoad(true)} data-testid="map-load-btn">Carica la mappa</button>
        {link && <a href={link} target="_blank" rel="noopener noreferrer" className="underline">Apri in Google Maps</a>}
      </div>
    );
  }
  return <iframe title="Mappa del centro" src={url} className="aspect-[4/3] w-full rounded-2xl border-0" loading="lazy" referrerPolicy="no-referrer" />;
}

export function Center() {
  const { site } = useSite();
  const page = useFetch("/public/pages/il-centro");
  const s = site || {};
  const photos = s.gallery_media_ids?.length ? s.gallery_media_ids.map((id) => mediaUrl(id, "lg")) : ["/img/centro-7278.webp", "/img/centro-7289.webp", "/img/centro-7284.webp", "/img/centro-7271.webp", "/img/centro-7265.webp"];
  return (
    <>
      <Seo title="Il centro" description="Indirizzo, orari e indicazioni per raggiungere il centro." path="/il-centro" />
      <PageHero eyebrow="Il centro" title={page.data?.title || "Il centro"} intro={page.data?.intro} crumbs={[{ label: "Il centro" }]} />
      <Container className="grid gap-4 py-12 sm:grid-cols-3">
        {photos.map((src, i) => <img key={src} src={src} alt={`Ambiente del centro ${i + 1}`} width={1200} height={1500} loading="lazy" className={`w-full rounded-2xl object-cover ${i === 0 ? "aspect-[4/5] sm:row-span-2 sm:h-full" : "aspect-[4/3]"}`} />)}
      </Container>
      <Container className="grid gap-14 pb-20 lg:grid-cols-2">
        <div>
          <Prose html={page.data?.body_html} />
          <ul className="mt-8 space-y-4">
            <li className="flex gap-3"><MapPin className="text-[var(--brass-text)]" /><span data-testid="center-address">{s.address_street}, {s.address_zip} {s.address_city} ({s.address_province})</span></li>
            <li className="flex gap-3"><Phone className="text-[var(--brass-text)]" />{s.phone}</li>
            <li className="flex gap-3"><Mail className="text-[var(--brass-text)]" />{s.email}</li>
          </ul>
          <h2 className="mt-10 flex items-center gap-2 text-3xl"><Clock size={22} /> Orari</h2>
          <dl className="mt-4 divide-y divide-[var(--line)]" data-testid="center-hours">{(s.opening_hours || []).map((h) => <div key={h.day} className="flex justify-between py-3"><dt>{h.day}</dt><dd className="text-[var(--muted-ink)]">{h.hours}</dd></div>)}</dl>
          <h2 className="mt-10 text-3xl">Come arrivare</h2>
          <Prose html={s.directions_html} className="mt-4" />
        </div>
        <MapEmbed url={s.map_embed_url} link={s.map_link_url} />
      </Container>
    </>
  );
}

export function Contact() {
  const { site } = useSite();
  const [f, setF] = useState({ name: "", email: "", phone: "", subject: "informazioni", message: "", privacy_ack: false, website: "" });
  const [status, setStatus] = useState(null);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setStatus({ busy: true });
    try { await api.post("/public/contact", f); setStatus({ ok: true }); } catch (err) { setStatus({ error: errorMessage(err) }); }
  };
  return (
    <>
      <Seo title="Contatti" description="Contatta il centro per informazioni o appuntamenti." path="/contatti" />
      <PageHero eyebrow="Contatti" title="Parliamone." intro="Scrivici per informazioni. Per favore non inserire dati sanitari nel messaggio: ne parleremo in visita." crumbs={[{ label: "Contatti" }]} />
      <Container className="grid gap-14 py-20 lg:grid-cols-5">
        <div className="space-y-4 lg:col-span-2">
          <p className="flex gap-3"><Phone className="text-[var(--brass-text)]" />{site?.phone}</p>
          <p className="flex gap-3"><Mail className="text-[var(--brass-text)]" />{site?.email}</p>
          <p className="flex gap-3"><MapPin className="text-[var(--brass-text)]" />{site?.address_street}, {site?.address_city}</p>
          <Link to="/prenota" className="btn-primary mt-6">Prenota online</Link>
        </div>
        {status?.ok ? <p className="card-soft p-8 lg:col-span-3" role="status" data-testid="contact-success">Grazie, il messaggio è stato inviato. Ti risponderemo al più presto.</p> : (
          <form onSubmit={submit} className="grid gap-5 lg:col-span-3" data-testid="contact-form">
            <div className="grid gap-5 sm:grid-cols-2">
              <label className="text-sm">Nome e cognome<input required className="field mt-1" value={f.name} onChange={set("name")} data-testid="contact-name" /></label>
              <label className="text-sm">Email<input required type="email" className="field mt-1" value={f.email} onChange={set("email")} data-testid="contact-email" /></label>
              <label className="text-sm">Telefono (facoltativo)<input className="field mt-1" value={f.phone} onChange={set("phone")} data-testid="contact-phone" /></label>
              <label className="text-sm">Argomento<select className="field mt-1" value={f.subject} onChange={set("subject")} data-testid="contact-subject"><option value="informazioni">Informazioni</option><option value="prenotazione">Prenotazione</option><option value="altro">Altro</option></select></label>
            </div>
            <label className="text-sm">Messaggio<textarea required rows={5} maxLength={2000} className="field mt-1" value={f.message} onChange={set("message")} data-testid="contact-message" /></label>
            <input type="text" tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden" value={f.website} onChange={set("website")} />
            <label className="flex gap-3 text-sm"><input type="checkbox" required checked={f.privacy_ack} onChange={set("privacy_ack")} data-testid="contact-privacy" /> Ho letto l'<Link to="/privacy" className="underline">informativa privacy</Link>.</label>
            {status?.error && <p role="alert" className="text-sm text-red-700" data-testid="contact-error">{status.error}</p>}
            <button className="btn-primary w-fit" disabled={status?.busy} data-testid="contact-submit">Invia messaggio</button>
          </form>
        )}
      </Container>
    </>
  );
}

export function Faq() {
  const { data, loading } = useFetch("/public/faqs");
  if (loading) return <Loading />;
  const groups = [...new Set((data || []).map((f) => f.group || "Generale"))];
  const ld = { "@context": "https://schema.org", "@type": "FAQPage", mainEntity: (data || []).map((f) => ({ "@type": "Question", name: f.question, acceptedAnswer: { "@type": "Answer", text: f.answer } })) };
  return (
    <>
      <Seo title="Domande frequenti" description="Risposte alle domande più comuni su trattamenti, prenotazioni e privacy." path="/faq" jsonLd={ld} />
      <PageHero eyebrow="FAQ" title="Domande frequenti" crumbs={[{ label: "FAQ" }]} />
      <Container className="max-w-3xl py-20">
        {groups.map((g) => (
          <section key={g} className="mb-12"><h2 className="text-3xl">{g}</h2>
            <Accordion type="single" collapsible className="mt-4">{data.filter((f) => (f.group || "Generale") === g).map((f) => (
              <AccordionItem key={f.id} value={f.id}><AccordionTrigger className="text-left text-base" data-testid={`faq-${f.id}`}>{f.question}</AccordionTrigger><AccordionContent className="text-[var(--muted-ink)]">{f.answer}</AccordionContent></AccordionItem>
            ))}</Accordion>
          </section>
        ))}
      </Container>
    </>
  );
}

export function Guides() {
  const { data, loading } = useFetch("/public/guides");
  if (loading) return <Loading />;
  return (
    <>
      <Seo title="Guide informative" description="Guide su trattamenti, prezzi e durata dei risultati." path="/guide" />
      <PageHero eyebrow="Guide" title="Guide informative" intro="Approfondimenti per arrivare alla visita con le idee chiare." crumbs={[{ label: "Guide" }]} />
      <Container className="grid gap-6 py-20 md:grid-cols-3">
        {(data || []).map((g) => <Link key={g.id} to={`/guide/${g.slug}`} className="card-soft p-7" data-testid={`guide-card-${g.slug}`}><p className="eyebrow">{g.guide_type}</p><h2 className="mt-3 text-2xl">{g.title}</h2><p className="mt-3 text-sm text-[var(--muted-ink)]">{g.excerpt}</p></Link>)}
      </Container>
    </>
  );
}

export function Guide() {
  const { slug } = useParams();
  const { data: g, error, loading } = useFetch(`/public/guides/${slug}`, [slug]);
  if (loading) return <Loading />;
  if (error) return <NotFound />;
  return (
    <>
      <Seo title={g.seo_title || g.title} description={g.seo_description || g.excerpt} path={`/guide/${g.slug}`} jsonLd={{ "@context": "https://schema.org", "@type": "Article", headline: g.title }} />
      <PageHero eyebrow={`Guida · ${g.guide_type}`} title={g.title} intro={g.excerpt} crumbs={[{ label: "Guide", to: "/guide" }, { label: g.title }]} />
      <Container className="grid gap-14 py-20 lg:grid-cols-3">
        <Prose html={g.body_html} className="lg:col-span-2" />
        <aside className="card-soft h-fit p-6"><p className="eyebrow">Trattamenti collegati</p>
          <ul className="mt-4 space-y-2">{g.treatments.map((t) => <li key={t.slug}><Link to={treatmentPath(t)} className="text-[var(--petrol)] hover:underline" data-testid={`guide-treatment-${t.slug}`}>{t.name}</Link></li>)}</ul>
        </aside>
      </Container>
    </>
  );
}

export function Glossary() {
  const { data, loading } = useFetch("/public/glossary");
  if (loading) return <Loading />;
  const sorted = [...(data || [])].sort((a, b) => a.term.localeCompare(b.term, "it"));
  return (
    <>
      <Seo title="Glossario" description="I termini della medicina estetica spiegati in modo semplice." path="/glossario" />
      <PageHero eyebrow="Glossario" title="Le parole della medicina estetica" crumbs={[{ label: "Glossario" }]} />
      <Container className="max-w-3xl py-20"><dl className="divide-y divide-[var(--line)]">{sorted.map((t) => (
        <div key={t.id} id={t.slug} className="py-6" data-testid={`glossary-${t.slug}`}><dt className="font-serif text-2xl">{t.term}</dt><dd className="mt-2 text-[var(--muted-ink)]">{t.definition}</dd></div>
      ))}</dl></Container>
    </>
  );
}

export function Legal({ pageKey, fallbackTitle }) {
  const { data, error, loading } = useFetch(`/public/pages/${pageKey}`, [pageKey]);
  if (loading) return <Loading />;
  return (
    <>
      <Seo title={data?.title || fallbackTitle} path={`/${pageKey}`} />
      <PageHero eyebrow="Informazioni legali" title={data?.title || fallbackTitle} intro={data?.intro} crumbs={[{ label: fallbackTitle }]} />
      <Container className="max-w-3xl py-20">
        {error ? <p className="card-soft p-8" data-testid="legal-draft-notice">Documento in preparazione: il testo è in bozza e verrà pubblicato dopo la revisione.</p> : <Prose html={data.body_html} />}
      </Container>
    </>
  );
}

export function SiteMap() {
  const { catalog } = useSite();
  const guides = useFetch("/public/guides");
  const base = [["/", "Home"], ["/chi-siamo", "Chi siamo"], ["/trattamenti", "Tutti i trattamenti"], ["/listino-prezzi", "Listino prezzi"], ["/galleria", "Prima e dopo"], ["/il-centro", "Il centro"], ["/contatti", "Contatti"], ["/prenota", "Prenota"], ["/faq", "FAQ"], ["/guide", "Guide"], ["/glossario", "Glossario"], ["/privacy", "Privacy"], ["/cookie", "Cookie"], ["/termini", "Termini"]];
  return (
    <>
      <Seo title="Mappa del sito" path="/mappa-del-sito" />
      <PageHero eyebrow="Navigazione" title="Mappa del sito" crumbs={[{ label: "Mappa del sito" }]} />
      <Container className="grid gap-10 py-20 md:grid-cols-3">
        <ul className="space-y-2">{base.map(([to, l]) => <li key={to}><Link to={to} className="hover:text-[var(--petrol)]">{l}</Link></li>)}</ul>
        <div>{(catalog?.categories || []).map((c) => (
          <div key={c.id} className="mb-6"><Link to={`/trattamenti/${c.slug}`} className="font-serif text-xl">{c.name}</Link>
            <ul className="mt-2 space-y-1 pl-4 text-sm">{catalog.treatments.filter((t) => t.category_id === c.id).map((t) => <li key={t.id}><Link to={treatmentPath(t)} className="hover:text-[var(--petrol)]">{t.name}</Link></li>)}</ul></div>
        ))}</div>
        <ul className="space-y-2 text-sm"><li className="font-serif text-xl">Guide</li>{(guides.data || []).map((g) => <li key={g.id}><Link to={`/guide/${g.slug}`} className="hover:text-[var(--petrol)]">{g.title}</Link></li>)}</ul>
      </Container>
    </>
  );
}

export function Gallery() {
  const [params, setParams] = useSearchParams();
  const filter = params.get("trattamento") || "";
  const [state, setState] = useState({ items: [], page: 0, pages: 1, filters: [] });
  const [busy, setBusy] = useState(false);
  const load = async (page, reset) => {
    setBusy(true);
    const r = await api.get("/public/gallery", { params: { page, per: 6, ...(filter ? { treatment: filter } : {}) } });
    setState((s) => ({ ...r.data, items: reset ? r.data.items : [...s.items, ...r.data.items], filters: r.data.filters.length ? r.data.filters : s.filters }));
    setBusy(false);
  };
  useEffect(() => {
    load(1, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);
  return (
    <>
      <Seo title="Galleria prima e dopo" description="Casi reali autorizzati alla pubblicazione, mostrati senza ritocchi." path="/galleria" />
      <PageHero eyebrow="Galleria" title="Prima e dopo" intro="Ogni caso è pubblicato con autorizzazione del paziente. I risultati variano da persona a persona." crumbs={[{ label: "Prima e dopo" }]} />
      <Container className="py-14">
        <div className="flex flex-wrap gap-3" role="group" aria-label="Filtra per trattamento">
          {[{ name: "Tutti", slug: "" }, ...state.filters].map((f) => (
            <button key={f.slug} aria-pressed={filter === f.slug} onClick={() => setParams(f.slug ? { trattamento: f.slug } : {})} className={`rounded-full border px-5 py-2 text-sm transition-colors ${filter === f.slug ? "border-[var(--petrol)] bg-[var(--petrol)] text-white" : "border-[var(--line)] bg-white hover:border-[var(--brass)]"}`} data-testid={`gallery-filter-${f.slug || "all"}`}>{f.name}</button>
          ))}
        </div>
        {state.items.length === 0 && !busy && <p className="mt-12 text-[var(--muted-ink)]" data-testid="gallery-empty">Nessun caso pubblicato al momento.</p>}
        <div className="mt-10 grid gap-10 sm:grid-cols-2 lg:grid-cols-3">
          {state.items.map((c) => (
            <figure key={c.id} data-testid={`gallery-case-${c.id}`}>
              <BeforeAfter beforeId={c.before_id} afterId={c.after_id} beforeAlt={c.before_alt} afterAlt={c.after_alt} testid={`ba-${c.id}`} />
              <figcaption className="mt-4"><p className="font-serif text-2xl">{c.title}</p>
                {c.treatment && <Link to={`/trattamenti/x/${c.treatment.slug}`} className="eyebrow hover:underline">{c.treatment.name}</Link>}
                <p className="mt-2 text-sm text-[var(--muted-ink)]">{c.description}</p></figcaption>
            </figure>
          ))}
        </div>
        {state.page < state.pages && <div className="mt-12 text-center"><button className="btn-outline" disabled={busy} onClick={() => load(state.page + 1, false)} data-testid="gallery-load-more">Carica altri casi</button></div>}
      </Container>
    </>
  );
}
